import React, { FunctionComponent, useMemo, ReactNode } from 'react'
import clsx from 'clsx'

import { useIsBreakpointMedium, useIsBreakpointLarge } from '../useBreakpoint/utils'

import { getGridConfig } from './utils'
import { MIN_WIDTH_GRID_ITEM, GUTTER_VARIANT, CONTENT_ALIGNMENT_VARIANT, ColumnsTypes, GutterVariantTypes, ContentAlignmentTypes } from './constants'
import styles from './style.module.scss'
import { useThemeClass } from '../Theme'

export interface GridItemProps {
  sm?: ColumnsTypes
  md?: ColumnsTypes
  lg?: ColumnsTypes
  children: ReactNode
}

export interface GridFixedProps {
  width?: number
  align?: 'left' | 'right' | 'center'
  children?: ReactNode
}
export interface GridProps {
  gutter?:
    | {
        sm?: GutterVariantTypes
        md?: GutterVariantTypes
        lg?: GutterVariantTypes
      }
    | GutterVariantTypes
  verticalGutter?:
    | {
        sm?: GutterVariantTypes
        md?: GutterVariantTypes
        lg?: GutterVariantTypes
      }
    | GutterVariantTypes
  verticalGap?:
    | {
        sm?: GutterVariantTypes
        md?: GutterVariantTypes
        lg?: GutterVariantTypes
      }
    | GutterVariantTypes
  center?: boolean
  justifyContent?: ContentAlignmentTypes
  children: ReactNode
}

export type GridItemComponent = FunctionComponent<GridItemProps>
export type GridFixedItemComponent = FunctionComponent<GridFixedProps>
export type GridComponent = FunctionComponent<GridProps> & {
  Item: GridItemComponent
  FixedItem: GridFixedItemComponent
}

export const Grid: GridComponent = ({
  gutter = GUTTER_VARIANT.STANDARD,
  verticalGutter = GUTTER_VARIANT.NONE,
  verticalGap,
  center = false,
  justifyContent = CONTENT_ALIGNMENT_VARIANT.SPACE_BETWEEN,
  children,
}) => {
  const themeClass = useThemeClass()
  const isMediumBp = useIsBreakpointMedium()
  const isLargeBp = useIsBreakpointLarge()

  const gridSpacing = (
    gutter:
      | {
          sm?: GutterVariantTypes
          md?: GutterVariantTypes
          lg?: GutterVariantTypes
        }
      | GutterVariantTypes,
    isVerticalGutter = false
  ): string => {
    const isSpacingStatic = (
      gutter?: { sm?: GutterVariantTypes; md?: GutterVariantTypes; lg?: GutterVariantTypes } | GutterVariantTypes
    ): gutter is GutterVariantTypes => {
      return typeof gutter !== 'object'
    }
    if (isSpacingStatic(gutter)) {
      return String(gutter)
    } else {
      return getGridConfig<GutterVariantTypes | undefined>(
        {
          isLargeBp,
          isMediumBp,
          lg: gutter?.lg,
          md: gutter?.md,
          sm: gutter?.sm,
        },
        isVerticalGutter
      )
    }
  }

  const verticalGapValue = verticalGap && gridSpacing(verticalGap, true)
  const className = clsx(
    styles.grid,
    styles[`gutter--${gridSpacing(gutter).replace('.', '-')}`],
    styles[`vertical--gutter--${gridSpacing(verticalGutter, true).replace('.', '-')}`],
    verticalGapValue && styles[`vertical--gap--${verticalGapValue.replace('.', '-')}`],
    center && styles.center,
    justifyContent === CONTENT_ALIGNMENT_VARIANT.NONE && styles.justifyContentStart,
    styles[themeClass]
  )

  return <div className={className}>{children}</div>
}

const GridItem: GridItemComponent = ({ children, sm, md, lg, ...otherProps }) => {
  const isMediumBp = useIsBreakpointMedium()
  const isLargeBp = useIsBreakpointLarge()

  const gridColumns = useMemo(() => {
    return getGridConfig<ColumnsTypes | undefined>({
      isLargeBp,
      isMediumBp,
      lg,
      md,
      sm,
    })
  }, [isLargeBp, isMediumBp, lg, md, sm])

  const gridClassNames = clsx(styles.fluid, styles[`grid--column--${gridColumns}`])

  return (
    <div className={gridClassNames} {...otherProps}>
      {children}
    </div>
  )
}

const GridFixedItem: GridFixedItemComponent = ({ children, width = MIN_WIDTH_GRID_ITEM, align = 'left', ...otherProps }) => {
  const remValue: number = width / 16
  const gridClassNames = clsx(styles.fixed, align === 'right' && styles.alignRight, align === 'center' && styles.alignCenter)
  return (
    <div
      className={gridClassNames}
      style={{
        width: `${remValue}rem`,
      }}
      {...otherProps}
    >
      {children}
    </div>
  )
}

Grid.Item = GridItem
Grid.FixedItem = GridFixedItem
